import React, {useCallback, useState} from "react";
import {useApolloClient} from "@apollo/client";
import {useErrorReporting} from "@unibuddy/error-reporting";
import {Box} from "@unibuddy/patron";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";

import {
    SHOW_CONVERSATION_FEEDBACK,
    UB_CONVERSATION_LEAD_GENERATOR_ENABLED,
} from "../../../../utils/featureFlags";
import {
    GetAssistantConversationDocument,
    GetAssistantConversationQuery,
    GetAssistantConversationQueryVariables,
    Maybe,
    SaveAssistantConversationFeedbackMutationVariables,
    useSaveAssistantConversationEmailLeadMutation,
    useSaveAssistantConversationFeedbackMutation,
} from "../../../../types/generated";
import {useUniversityContext} from "../../../../providers/UniversityProvider";
import {getErrorString} from "../../../../utils/getErrorString";
import {useAssistantInitiationContext} from "../../providers/AssistantInitiationProvider";
import {ConversationPopupWrapper} from "./components/ConversationPopupWrapper/ConversationPopupWrapper";
import {ConversationLeadForm} from "./components/ConversationLead/ConversationLeadForm";
import {ConversationFeedbackFreeTextForm} from "./components/ConversationFeedback/ConversationFeedbackFreeTextForm";
import {ConversationFeedbackRatingForm} from "./components/ConversationFeedback/ConversationFeedbackRatingForm";
import {ConversationPopupThankYou} from "./components/ConversationPopupThankYou/ConversationPopupThankYou";

enum ConversationPopupView {
    LEAD_EMAIL,
    FEEDBACK_RATING,
    FEEDBACK_TEXT,
    THANK_YOU,
}

export function getConversationPopupDismissalStorageKey(
    universityId: string,
    conversationId: string,
) {
    return `ASSISTANT_CONVERSATION_POPUP_DISMISSED_${universityId}_${conversationId}`;
}

export function ConversationPopup() {
    const client = useApolloClient();
    const {reportError} = useErrorReporting();

    const {universityId} = useUniversityContext();
    const {conversationId, feedback, leadGenerated, token} = useAssistantInitiationContext();

    const showConversationLeadGenerator = useBooleanFeatureFlag(
        UB_CONVERSATION_LEAD_GENERATOR_ENABLED.key,
        UB_CONVERSATION_LEAD_GENERATOR_ENABLED.defaultValue,
    );
    const showConversationFeedbackFlag = useBooleanFeatureFlag(
        SHOW_CONVERSATION_FEEDBACK.key,
        SHOW_CONVERSATION_FEEDBACK.defaultValue,
    );

    const [view, setView] = useState<ConversationPopupView | null>(() => {
        if (showConversationLeadGenerator && !leadGenerated) {
            return ConversationPopupView.LEAD_EMAIL;
        }
        if (showConversationFeedbackFlag && !feedback) {
            return ConversationPopupView.FEEDBACK_RATING;
        }
        return null;
    });

    const [hidePopups, setHidePopups] = useState<boolean>(() => {
        if (!conversationId) return false;
        const popupDismissalStorageKey = getConversationPopupDismissalStorageKey(
            universityId,
            conversationId,
        );
        return window.sessionStorage.getItem(popupDismissalStorageKey) === "true";
    });

    const [saveAssistantConversationEmailLead] = useSaveAssistantConversationEmailLeadMutation();

    const [hasFeedback, setHasFeedback] = useState<boolean>(!!feedback);
    const [savedRating, setSavedRating] = useState<Maybe<number>>(null);
    const [saveConversationFeedback] = useSaveAssistantConversationFeedbackMutation();

    const handleLeadEmailSubmit = React.useCallback(
        async (email: string, ubConsent: boolean) => {
            if (!conversationId) return;
            const variables = {
                conversationId,
                universityId,
                email,
                ubConsent,
                token,
            };
            try {
                await saveAssistantConversationEmailLead({
                    variables,
                });
            } catch (e) {
                console.error("Failed to submit email lead, try again later", e);
                reportError(
                    new Error("Failed to submit email lead, try again later" + getErrorString(e)),
                );
            }
        },
        [conversationId, reportError, saveAssistantConversationEmailLead, universityId, token],
    );

    const handleFeedbackSubmit = useCallback(
        async (
            feedback: Pick<SaveAssistantConversationFeedbackMutationVariables, "rating" | "text">,
        ) => {
            if (!conversationId) return;
            const variables = {
                conversationId,
                universityId,
                token,
                ...feedback,
            };
            try {
                const feedback = await saveConversationFeedback({
                    variables,
                });

                if (feedback?.data?.saveAssistantConversationFeedback.rating) {
                    const result = client.readQuery<
                        GetAssistantConversationQuery,
                        GetAssistantConversationQueryVariables
                    >({
                        query: GetAssistantConversationDocument,
                        variables: {
                            id: conversationId,
                            universityId,
                            token,
                        },
                    });

                    if (result?.getAssistantConversation) {
                        client.writeQuery<
                            GetAssistantConversationQuery,
                            GetAssistantConversationQueryVariables
                        >({
                            query: GetAssistantConversationDocument,
                            variables: {
                                id: conversationId,
                                universityId,
                                token,
                            },
                            data: {
                                ...result,
                                getAssistantConversation: {
                                    ...result.getAssistantConversation,
                                    feedback: {
                                        rating: variables.rating,
                                    },
                                },
                            },
                        });
                    }
                }
            } catch (e) {
                console.error("Failed to submit conversation feedback, try again later", e);
                reportError(
                    new Error(
                        "Failed to submit conversation feedback, try again later" + (e as string),
                    ),
                );
            }
        },
        /* Excluding the mutation */
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
        [client, conversationId, reportError, universityId, token],
    );

    const handleDismiss = () => {
        if (!conversationId) return;
        const popupDismissalStorageKey = getConversationPopupDismissalStorageKey(
            universityId,
            conversationId,
        );
        window.sessionStorage.setItem(popupDismissalStorageKey, "true");
        setHidePopups(true);
    };

    const handleLeadEmailSkip = () => {
        const showConversationFeedback = showConversationFeedbackFlag && !hasFeedback;
        if (showConversationFeedback) {
            setView(ConversationPopupView.FEEDBACK_RATING);
        } else {
            setView(null);
            setHidePopups(true);
        }
    };

    const onSubmitLeadEmail = React.useCallback(
        async (email: string, ubConsent: boolean) => {
            const showConversationFeedback = showConversationFeedbackFlag && !hasFeedback;

            await handleLeadEmailSubmit(email, ubConsent);

            if (showConversationFeedback) {
                setView(ConversationPopupView.FEEDBACK_RATING);
            } else {
                setView(ConversationPopupView.THANK_YOU);
                setTimeout(() => {
                    setHidePopups(true);
                }, 5000);
            }
        },
        [handleLeadEmailSubmit, hasFeedback, showConversationFeedbackFlag],
    );

    const onSubmitFeedbackRating = useCallback(
        async (rating: number) => {
            setSavedRating(rating);
            await handleFeedbackSubmit({rating});
            setView(ConversationPopupView.FEEDBACK_TEXT);
        },
        [handleFeedbackSubmit],
    );

    const onSubmitFeedbackText = useCallback(
        async (freeText: string) => {
            if (!savedRating) return;
            await handleFeedbackSubmit({text: freeText, rating: savedRating});
            setView(ConversationPopupView.THANK_YOU);
            setTimeout(() => {
                setHasFeedback(true);
                setHidePopups(true);
            }, 5000);
        },
        [savedRating, handleFeedbackSubmit],
    );

    if (!conversationId) return null;
    if (hidePopups) return null;

    switch (view) {
        case ConversationPopupView.LEAD_EMAIL:
            return (
                <ConversationPopupWrapper>
                    <ConversationLeadForm
                        handleSubmit={onSubmitLeadEmail}
                        handleDismiss={handleLeadEmailSkip}
                    />
                </ConversationPopupWrapper>
            );

        case ConversationPopupView.FEEDBACK_RATING:
            return (
                <ConversationPopupWrapper>
                    <ConversationFeedbackRatingForm
                        handleSubmit={onSubmitFeedbackRating}
                        handleDismiss={handleDismiss}
                    />
                </ConversationPopupWrapper>
            );

        case ConversationPopupView.FEEDBACK_TEXT:
            return (
                <ConversationPopupWrapper>
                    <ConversationFeedbackFreeTextForm
                        handleSubmit={onSubmitFeedbackText}
                        handleDismiss={handleDismiss}
                    />
                </ConversationPopupWrapper>
            );

        case ConversationPopupView.THANK_YOU:
            return (
                <Box padding="medium" bgColor="white" borderBottomWidth={1} borderColor="grey100">
                    <ConversationPopupThankYou />
                </Box>
            );

        default:
            return null;
    }
}
